/**
 * DialogProvider — accessible in-app modal replacements for
 * window.confirm / window.prompt.
 *
 * Usage:
 *   1. Mount <DialogProvider> around your app (or the authenticated layout).
 *   2. Inside any child component:
 *      const askConfirm = useConfirm();
 *      const askPrompt  = usePrompt();
 *
 *      const ok = await askConfirm({ title: 'Delete?', destructive: true });
 *      const lang = await askPrompt({ title: 'Add language', label: 'Code' });
 *
 * Accessibility contract:
 *  - Rendered via React portal to document.body.
 *  - role="alertdialog" for confirm / role="dialog" for prompt.
 *  - aria-modal, aria-labelledby, aria-describedby wired to element ids.
 *  - Focus trap (Tab / Shift+Tab cycle within dialog).
 *  - ESC key and backdrop click cancel.
 *  - On open: focus moves to cancel button (confirm) or input (prompt).
 *  - On close: focus restored to the previously focused element.
 *  - Body scroll locked while a dialog is open.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface PromptOptions {
  title: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
}

type DialogState =
  | { type: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { type: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void }
  | null;

interface DialogContextValue {
  open: (state: DialogState) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DialogContext = createContext<DialogContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<DialogState>(null);

  const open = useCallback((state: DialogState) => {
    setDialog(state);
  }, []);

  const close = useCallback(() => {
    setDialog(null);
  }, []);

  // Lock body scroll while a dialog is open
  useEffect(() => {
    if (!dialog) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [dialog]);

  return (
    <DialogContext.Provider value={{ open }}>
      {children}
      {dialog?.type === 'confirm' && (
        <ConfirmDialog
          opts={dialog.opts}
          onConfirm={() => {
            dialog.resolve(true);
            close();
          }}
          onCancel={() => {
            dialog.resolve(false);
            close();
          }}
        />
      )}
      {dialog?.type === 'prompt' && (
        <PromptDialog
          opts={dialog.opts}
          onConfirm={(value) => {
            dialog.resolve(value);
            close();
          }}
          onCancel={() => {
            dialog.resolve(null);
            close();
          }}
        />
      )}
    </DialogContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Returns a function that shows an accessible confirm dialog and resolves a Promise<boolean>. */
// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useConfirm must be used inside <DialogProvider>');

  return useCallback(
    (opts: ConfirmOptions): Promise<boolean> =>
      new Promise((resolve) => {
        ctx.open({ type: 'confirm', opts, resolve });
      }),
    [ctx],
  );
}

/** Returns a function that shows an accessible prompt dialog and resolves a Promise<string | null>. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePrompt() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('usePrompt must be used inside <DialogProvider>');

  return useCallback(
    (opts: PromptOptions): Promise<string | null> =>
      new Promise((resolve) => {
        ctx.open({ type: 'prompt', opts, resolve });
      }),
    [ctx],
  );
}

// ---------------------------------------------------------------------------
// Focus trap utility
// ---------------------------------------------------------------------------

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function trapFocus(el: HTMLElement, e: KeyboardEvent) {
  const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (nodes.length < 2) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (!first || !last) return;
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// ---------------------------------------------------------------------------
// ConfirmDialog
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  opts: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ opts, onConfirm, onCancel }: ConfirmDialogProps) {
  const { title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = false } = opts;
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<Element | null>(null);
  const titleId = useId();
  const bodyId = useId();

  // Save previously focused element and move focus to cancel button on open
  useEffect(() => {
    previousFocus.current = document.activeElement;
    cancelBtnRef.current?.focus();

    return () => {
      if (previousFocus.current instanceof HTMLElement) {
        previousFocus.current.focus();
      }
    };
  }, []);

  // ESC key handler + focus trap
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        trapFocus(dialogRef.current, e);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const confirmCls = destructive
    ? 'rounded-md border border-red-300 bg-card px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950'
    : 'rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600';

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        {body && (
          <p id={bodyId} className="mt-2 whitespace-pre-line text-sm text-muted">
            {body}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/10"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={confirmCls}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// PromptDialog
// ---------------------------------------------------------------------------

interface PromptDialogProps {
  opts: PromptOptions;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

function PromptDialog({ opts, onConfirm, onCancel }: PromptDialogProps) {
  const { title, label, placeholder, initialValue = '', confirmLabel = 'OK' } = opts;
  const [value, setValue] = useState(initialValue);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocus = useRef<Element | null>(null);
  const titleId = useId();
  const inputId = useId();

  // Save previously focused element and move focus to input on open
  useEffect(() => {
    previousFocus.current = document.activeElement;
    inputRef.current?.focus();

    return () => {
      if (previousFocus.current instanceof HTMLElement) {
        previousFocus.current.focus();
      }
    };
  }, []);

  // ESC key handler + focus trap
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        trapFocus(dialogRef.current, e);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {label && (
            <label htmlFor={inputId} className="block text-sm font-medium">
              {label}
            </label>
          )}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/10"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.trim()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
